/* eslint-disable import/no-extraneous-dependencies */ // The libs are included..
import { PlayerManager } from '@24i/react-native-player';
import { useEventListener } from '@24i/player-utils-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StreamType } from 'Types';
import { isStreamTimeshift, isStreamLive } from '../../../../../../utils';
import { useChromecastContext } from 'LocalGluons/components/ChromecastContext';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useCanSeek = (
    playerInstance: PlayerManager,
    streamType: StreamType,
    elapsedTime: number,
    canSkipAd: boolean
) => {
    const { mediaState, client } = useChromecastContext();
    const extractWindowEnd = useCallback(() => {
        // durationTime of hook usePlayerTimes does not return DVR window information
        if (!playerInstance) return 0;
        if (!playerInstance.live) return playerInstance.duration;
        return playerInstance.timeshiftRange.end;
    }, [playerInstance]);
    const [windowEnd, setWindowEnd] = useState(extractWindowEnd);
    const refreshWindowEnd = useCallback(() => {
        setWindowEnd(extractWindowEnd());
    }, [extractWindowEnd]);
    useEventListener('loadedmetadata', refreshWindowEnd, playerInstance);
    useEventListener('timeupdate', refreshWindowEnd, playerInstance);

    const atLeast30SecondsRemains = useMemo(() => {
        if (client) return mediaState.isSeekableForward;
        return Boolean(windowEnd - elapsedTime >= 30000);
    }, [elapsedTime, windowEnd, mediaState]);

    const atLeast30SecondsPassed = useMemo(() => Boolean(elapsedTime >= 30000), [elapsedTime]);

    const canSeekBackwards = useMemo(
        () => atLeast30SecondsPassed || isStreamLive({ streamType }),
        [streamType, atLeast30SecondsPassed]
    );

    const canSeekForwards = useMemo(
        () =>
            (isStreamTimeshift({ streamType }) && canSkipAd) ||
            (!isStreamLive({ streamType }) && atLeast30SecondsRemains && canSkipAd),
        [streamType, atLeast30SecondsRemains, canSkipAd]
    );

    return {
        canSeekBackwards,
        canSeekForwards
    };
};

export default useCanSeek;
